import { Button, FormControl, FormLabel, Input, Option, Select, Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { AdminLevelAutocomplete } from '@/components/form-elements/AdminLevelAutocomplete';
import { ButtonLink } from '@/components/router/ButtonLink';
import { I18N_DE } from '@/i18n/de';
import type { AdminLevel, CreateAdminLevelUserDto } from '@/modules/api';
import { AdminLevelUserRole, AdminLevelUserStatus } from '@/modules/api';
import {
  backendApiAdminLevelsCreateAdminLevelUserMutation,
  backendApiUsersFindOneOptions,
} from '@/modules/api/@tanstack/react-query.gen';

export const Route = createFileRoute('/verwaltung/_global/benutzerinnen/$userId/verwaltungsebenen/hinzufuegen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

type FormData = Pick<CreateAdminLevelUserDto, 'role' | 'status'>;

function RouteComponent() {
  const params = Route.useParams();
  const navigate = Route.useNavigate();

  const [adminLevel, setAdminLevel] = useState<AdminLevel>();

  const { data: user, error } = useQuery(
    backendApiUsersFindOneOptions({
      path: {
        userId: params.userId,
      },
    }),
  );

  const { mutate } = useMutation({
    ...backendApiAdminLevelsCreateAdminLevelUserMutation(),
    // mutationFn: ({ adminLevelId, ...requestBody }: { adminLevelId: string } & CreateAdminLevelUserDto) =>
    // AdminLevelsService.createAdminLevelUser({ adminLevelId, userId: params.userId, requestBody }),
    onSuccess: async (result) => {
      navigate({
        to: '/verwaltung/$adminLevelId/benutzerinnen/$userId',
        params: {
          adminLevelId: result.adminLevelId,
          userId: result.userId,
        },
      });
    },
  });

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: { role: AdminLevelUserRole.EDITOR, status: AdminLevelUserStatus.INACTIVE },
  });

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (!user) {
    return null;
  }

  return (
    <Stack
      component="form"
      gap={2}
      onSubmit={handleSubmit((formData) => {
        if (!adminLevel) {
          throw new Error('No `adminLevel` selected');
        }

        mutate({
          path: {
            adminLevelId: adminLevel.id,
            userId: params.userId,
          },
          body: {
            ...formData,
          },
        });
      })}
    >
      <Typography level="h3">Verwaltungsebene zuordnen</Typography>

      <FormControl>
        <FormLabel>Vorname</FormLabel>
        <Input readOnly value={user.firstName} />
      </FormControl>

      <FormControl>
        <FormLabel>Nachname</FormLabel>
        <Input readOnly value={user.lastName} />
      </FormControl>

      <FormControl>
        <FormLabel>Verwaltungsebene</FormLabel>
        <AdminLevelAutocomplete
          excludeIds={user.adminLevels?.map((adminLevelUser) => adminLevelUser.adminLevelId) ?? []}
          onChange={(_, value) => {
            setAdminLevel(value ?? undefined);
          }}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Rolle</FormLabel>
        <Controller
          control={control}
          name="role"
          render={({ field }) => (
            <Select {...field} onChange={(_, value) => field.onChange(value)}>
              {Object.values(AdminLevelUserRole).map((adminLevelUserRole) => (
                <Option key={adminLevelUserRole} value={adminLevelUserRole}>
                  {I18N_DE.AdminLevelUserRole[adminLevelUserRole]}
                </Option>
              ))}
            </Select>
          )}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Status</FormLabel>

        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <Select {...field} onChange={(_, value) => field.onChange(value)}>
              {Object.values(AdminLevelUserStatus).map((adminLevelUserStatus) => (
                <Option key={adminLevelUserStatus} value={adminLevelUserStatus}>
                  {I18N_DE.AdminLevelUserStatus[adminLevelUserStatus]}
                </Option>
              ))}
            </Select>
          )}
        />
      </FormControl>

      <Stack direction="row" gap={2} justifyContent="space-between" marginTop={4}>
        <ButtonLink params={{ userId: params.userId }} to="/verwaltung/benutzerinnen/$userId" variant="outlined">
          Benutzer*in
        </ButtonLink>
        <Button disabled={!adminLevel} type="submit">
          Hinzufügen
        </Button>
      </Stack>
    </Stack>
  );
}
